// Phase 2: マスター層 write 経路の新画面（隠しページ）
// ルート: /lesson-v2  ※サイドバーには出さない、URL直打ちで到達
//
// 旧 LessonEditPage の `editarea schedule` レイアウトに揃え、
// 「LessonContent マスター」は右下 FAB → 中央モーダルで開く方式。
//
// 設計: lesson_model_redesign_memo.md  Phase 2 / 8.1 / 8.4 / 9.3

import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../../../components/Breadcrumbs";
import StatusMessage from "../../../components/common/StatusMessage";
import useIsMobile from "../../../hooks/useIsMobile";
import {
  fetchLessonContents,
  fetchStudiosList,
  fetchInstructorsList,
} from "../../../utils/lessonV2Api";
import LessonContentPanel from "./components/LessonContentPanel";
import LessonPlacementEditor from "./components/LessonPlacementEditor";
import LessonPageContentPanel from "./components/LessonPageContentPanel";

const getBreadcrumbs = (selectedStudio) => {
  const items = [
    { title: "Home", path: "/" },
    { title: "Lesson", path: "/lesson" },
  ];
  if (selectedStudio) {
    items.push({
      title: selectedStudio.name,
      path: `/lesson`,
    });
  }
  return items;
};

const LessonManagementPage = () => {
  const [studios, setStudios] = useState([]);
  const [selectedStudioId, setSelectedStudioId] = useState("");
  const [contents, setContents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  // 設定オーバーレイ（旧 ＋/✎ の2FAB・2モーダルを歯車1つに統合）
  // settingsTab: "master"=レッスン内容マスター / "page"=説明文・色凡例
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("master");
  const isMobile = useIsMobile();

  // ESC で設定オーバーレイを閉じる
  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setSettingsOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [settingsOpen]);

  // モーダル開閉時の背景スクロールロック
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    if (settingsOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = prev;
    };
  }, [settingsOpen]);

  const loadContents = useCallback(async () => {
    const data = await fetchLessonContents();
    setContents(data.lesson_contents || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInitial(true);
      try {
        const [studiosData, instructorsData] = await Promise.all([
          fetchStudiosList(),
          fetchInstructorsList(),
          loadContents(),
        ]);
        if (cancelled) return;
        const studiosList = studiosData.studios || [];
        setStudios(studiosList);
        if (studiosList.length > 0) {
          setSelectedStudioId(String(studiosList[0].id));
        }
        setInstructors(instructorsData.instructors || []);
      } catch (err) {
        if (!cancelled) {
          setMessage(`初期データの取得に失敗: ${err.message || err}`);
          setMessageType("error");
        }
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadContents]);

  const selectedStudio = studios.find(
    (s) => String(s.id) === String(selectedStudioId)
  );

  // 子コンポーネントからメッセージを受ける
  const handleChildMessage = useCallback((text, type = "success") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(""), 4000);
  }, []);

  // 保存ボタン押下：LessonPlacementEditor 内の handleSave を呼び出す
  const handleSubmit = () => {
    if (typeof window !== "undefined" && typeof window.__lessonPlacementSave === "function") {
      window.__lessonPlacementSave();
    }
  };

  // リセット：内容を再読み込み（再描画で破棄）
  const handleReset = () => {
    if (selectedStudioId) {
      // selectedStudioId を一度クリアして set し直すと LessonPlacementEditor が再ロードする
      const id = selectedStudioId;
      setSelectedStudioId("");
      setTimeout(() => setSelectedStudioId(id), 0);
    }
  };

  return (
    <div
      className="editarea schedule"
      style={isMobile ? { padding: "8px 10px" } : undefined}
    >
      <Breadcrumbs breadcrumbs={getBreadcrumbs(selectedStudio)} />
      <StatusMessage message={message} type={messageType} />

      <div className="form">
        {/* スタジオ選択（旧 LessonEditPage と同じ .studio-list ul li.active） */}
        <div className="studio-list">
          <ul>
            {studios.map((studio) => (
              <li
                key={studio.id}
                className={
                  String(selectedStudioId) === String(studio.id) ? "active" : ""
                }
                onClick={() => setSelectedStudioId(String(studio.id))}
                style={{ cursor: "pointer" }}
              >
                {studio.name}
              </li>
            ))}
          </ul>
        </div>

        {/* 曜日グリッド配置エディタ（旧 ScheduleTable と同マークアップ） */}
        {loadingInitial ? (
          <p>読み込み中…</p>
        ) : (
          selectedStudioId && (
            <LessonPlacementEditor
              studioId={selectedStudioId}
              contents={contents}
              instructors={instructors}
              busy={busy}
              setBusy={setBusy}
              onMessage={handleChildMessage}
              onContentsChanged={loadContents}
            />
          )
        )}
      </div>

      {selectedStudioId && (
        <div className="submit-area">
          <button type="reset" onClick={handleReset} disabled={busy}>
            Reset
          </button>
          <button type="submit" onClick={handleSubmit} disabled={busy}>
            Submit
          </button>
        </div>
      )}

      {/* FAB とモーダルは body 直下に portal で描画する。
          .editarea.schedule に backdrop-filter: blur(12px) があり、
          これが containing block を作るため、子要素として置くと
          position: fixed が editarea 基準になってしまう（viewport 基準にならない）。 */}
      {/* 歯車 FAB と設定モーダルは body 直下に portal で描画する。
          .editarea.schedule に backdrop-filter: blur(12px) があり、
          これが containing block を作るため、子要素として置くと
          position: fixed が editarea 基準になってしまう（viewport 基準にならない）。 */}
      {!settingsOpen &&
        createPortal(
          <button
            type="button"
            className="lesson-master-fab"
            onClick={() => setSettingsOpen(true)}
            aria-label="設定を開く"
            title="設定"
          >
            <FontAwesomeIcon icon={faGear} />
          </button>,
          document.body
        )}

      {settingsOpen &&
        createPortal(
          <div
            className="lesson-master-modal"
            role="dialog"
            aria-modal="true"
            aria-label="設定"
            onClick={() => setSettingsOpen(false)}
          >
            <div
              className="lesson-master-modal__panel"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="lesson-master-modal__close"
                onClick={() => setSettingsOpen(false)}
                aria-label="閉じる"
              >
                ×
              </button>

              <div className="lesson-master-modal__head">
                <span className="lesson-master-modal__title">
                  <FontAwesomeIcon icon={faGear} /> 設定
                </span>
              </div>

              <div className="lesson-master-modal__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={settingsTab === "master"}
                  className={`lesson-master-modal__tab${
                    settingsTab === "master" ? " is-active" : ""
                  }`}
                  onClick={() => setSettingsTab("master")}
                >
                  レッスン
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={settingsTab === "page"}
                  className={`lesson-master-modal__tab${
                    settingsTab === "page" ? " is-active" : ""
                  }`}
                  onClick={() => setSettingsTab("page")}
                >
                  説明
                </button>
              </div>

              <div className="lesson-master-modal__body">
                {/* 2ペインを同じグリッドセルに重ねて高さを「高い方のタブ」基準で固定
                    → タブ切替でモーダル枠が動かない */}
                <div className="lesson-master-modal__panes">
                  <div
                    className={`lesson-master-modal__pane${
                      settingsTab === "master" ? "" : " is-hidden"
                    }`}
                  >
                    <LessonContentPanel
                      contents={contents}
                      instructors={instructors}
                      onChanged={loadContents}
                      busy={busy}
                      setBusy={setBusy}
                    />
                  </div>
                  <div
                    className={`lesson-master-modal__pane${
                      settingsTab === "page" ? "" : " is-hidden"
                    }`}
                  >
                    <LessonPageContentPanel onMessage={handleChildMessage} />
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default LessonManagementPage;
